import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Table,
  Spinner
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faPlus,
  faTrash,
  faSave
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { purchaseOrderApi } from 'api/endpoints/PurchaseOrder';
import { categoryApi } from 'api/endpoints/Category';
import { getTenantFromToken } from 'utils/routeUtils';

const hasProductOptions = product => {
  if (!product) return false;
  if (
    product.has_option === true ||
    product.has_option === 'true' ||
    product.has_option === 1
  ) {
    return true;
  }
  let opts = product.options;
  if (typeof opts === 'string') {
    try {
      opts = JSON.parse(opts);
    } catch (e) {
      // ignore
    }
  }
  if (Array.isArray(opts) && opts.length > 0) return true;
  if (opts && typeof opts === 'object' && Object.keys(opts).length > 0)
    return true;
  return false;
};

const getProductOptionPrice = (product, optId) => {
  if (!product || !optId) return 0;
  let prices = product.option_prices;
  if (typeof prices === 'string') {
    try {
      prices = JSON.parse(prices);
    } catch (e) {
      // ignore
    }
  }
  if (!prices) return 0;

  if (Array.isArray(prices)) {
    const matched = prices.find(
      p => p && String(p.option_name) === String(optId)
    );
    if (matched) {
      return Number(matched.price) || Number(matched.price_per_unit) || 0;
    }
    const idx = Number(optId);
    if (!isNaN(idx) && prices[idx]) {
      const item = prices[idx];
      return (
        Number(item.price) || Number(item.price_per_unit) || Number(item) || 0
      );
    }
  }

  if (typeof prices === 'object') {
    const val = prices[optId];
    if (val !== undefined && val !== null) {
      if (typeof val === 'object') {
        return Number(val.price) || Number(val.price_per_unit) || 0;
      }
      return Number(val) || 0;
    }
    for (const key of Object.keys(prices)) {
      if (String(key) === String(optId)) {
        const innerVal = prices[key];
        if (typeof innerVal === 'object') {
          return Number(innerVal.price) || Number(innerVal.price_per_unit) || 0;
        }
        return Number(innerVal) || 0;
      }
    }
  }

  return 0;
};

export default function CreatePurchaseOrder() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;
  const tenant = getTenantFromToken();

  // ── Form State ────────────────────────────────────────────────────────────
  const [poDate, setPoDate] = useState(
    new Date().toISOString().substring(0, 10)
  );
  const [deliveryDate, setDeliveryDate] = useState('');
  const [supplierId, setSupplierId] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [paymentMode, setPaymentMode] = useState('');
  const [paymentDuration, setPaymentDuration] = useState('');
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState('draft');
  const [poCode, setPoCode] = useState('');

  // ── Dropdown Master Data ──────────────────────────────────────────────────
  const [warehouses, setWarehouses] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [categories, setCategories] = useState([]);

  // ── Items State ───────────────────────────────────────────────────────────
  const [items, setItems] = useState([
    {
      tempId: Date.now(),
      productCategoryId: '',
      productId: '',
      optionId: '',
      sku: '',
      quantity: '',
      uom: '',
      pricePerUnit: '',
      stockValue: 0,
      productList: [],
      optionsList: []
    }
  ]);

  // ── UI State ──────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // ── Load Metadata ─────────────────────────────────────────────────────────
  useEffect(() => {
    document.title = isEditMode
      ? 'Edit Purchase Order | Seedbomb'
      : 'Create Purchase Order | Seedbomb';
    const fetchMetadata = async () => {
      try {
        const [whRes, supRes, catRes] = await Promise.all([
          purchaseOrderApi.getWarehouses(),
          purchaseOrderApi.getSuppliers(),
          categoryApi.getCategoryList()
        ]);
        setWarehouses(whRes.data?.results || whRes.data || []);
        setSuppliers(supRes.data?.results || supRes.data || []);
        setCategories(catRes.data?.results || []);
      } catch (error) {
        toast.error('Failed to load initial form dropdowns');
      }
    };
    fetchMetadata();
  }, [isEditMode]);

  // ── Load PO for Editing or Next Code ──────────────────────────────────────
  useEffect(() => {
    if (isEditMode) {
      const fetchPO = async () => {
        setLoading(true);
        try {
          const response = await purchaseOrderApi.getPurchaseOrder(id);
          const data = response.data?.results;
          if (data) {
            setPoDate(data.po_date || '');
            setDeliveryDate(data.delivery_date || '');
            setSupplierId(data.supplier_id || '');
            setWarehouseId(data.warehouse_id || '');
            setPaymentMode(data.payment_mode || '');
            setPaymentDuration(data.payment_duration || '');
            setNotes(data.notes || '');
            setStatus(data.status || 'draft');
            setPoCode(data.purchase_order_code || '');

            if (data.items && data.items.length > 0) {
              const mappedItems = await Promise.all(
                data.items.map(async (item, index) => {
                  let products = [];
                  let options = [];
                  let selectedProduct = null;

                  if (item.product_category_id) {
                    try {
                      const prodRes = await purchaseOrderApi.getProductReport({
                        cursor: 0,
                        limit: 100,
                        filters: {
                          search_text: '',
                          start_date: '',
                          end_date: ''
                        },
                        category_id: item.product_category_id
                      });
                      products =
                        prodRes.data?.results?.data ||
                        prodRes.data?.results ||
                        [];
                      selectedProduct = products.find(
                        p => p.id === item.product_id
                      );
                    } catch (err) {
                      console.log(err);
                    }
                  }

                  if (selectedProduct) {
                    let productOptions = selectedProduct.options;
                    if (typeof productOptions === 'string') {
                      try {
                        productOptions = JSON.parse(productOptions);
                      } catch (e) {
                        console.log(e);
                      }
                    }
                    if (productOptions) {
                      if (Array.isArray(productOptions)) {
                        options = productOptions.map((opt, i) => {
                          const name =
                            opt.option_name || opt.name || `Option ${i + 1}`;
                          return {
                            value: name,
                            label: name
                          };
                        });
                      } else if (typeof productOptions === 'object') {
                        options = Object.keys(productOptions).map(key => {
                          const optVal = productOptions[key];
                          return {
                            value: key,
                            label:
                              typeof optVal === 'object'
                                ? optVal.option_name || optVal.name || key
                                : String(optVal || key)
                          };
                        });
                      }
                    }
                  }

                  return {
                    tempId: Date.now() + index,
                    productCategoryId: item.product_category_id || '',
                    productId: item.product_id || '',
                    optionId: item.option_id || '',
                    sku: item.sku || '',
                    quantity: Number(item.quantity) || 0,
                    uom: item.uom || '',
                    pricePerUnit: Number(item.price_per_unit) || 0,
                    stockValue: Number(item.stock_value) || 0,
                    productList: products,
                    optionsList: options
                  };
                })
              );
              setItems(mappedItems);
            }
          }
        } catch (error) {
          toast.error('Failed to load purchase order entry details');
        } finally {
          setLoading(false);
        }
      };
      fetchPO();
    } else {
      const fetchNextCode = async () => {
        try {
          const res = await purchaseOrderApi.getNextPoCode();
          setPoCode(res.data?.results?.next_code || '');
        } catch (error) {
          console.error('Could not fetch next PO code');
        }
      };
      fetchNextCode();
    }
  }, [id, isEditMode]);

  // ── Item Interactions ─────────────────────────────────────────────────────
  const handleAddItem = () => {
    setItems([
      ...items,
      {
        tempId: Date.now(),
        productCategoryId: '',
        productId: '',
        optionId: '',
        sku: '',
        quantity: '',
        uom: '',
        pricePerUnit: '',
        stockValue: 0,
        productList: [],
        optionsList: []
      }
    ]);
  };

  const handleRemoveItem = index => {
    if (items.length === 1) {
      toast.warning('At least one line item is required');
      return;
    }
    setItems(items.filter((_, idx) => idx !== index));
  };

  const handleCategoryChange = async (index, catId) => {
    const newItems = [...items];
    newItems[index] = {
      ...newItems[index],
      productCategoryId: catId,
      productId: '',
      optionId: '',
      pricePerUnit: 0,
      stockValue: 0,
      uom: '',
      productList: [],
      optionsList: []
    };
    if (catId) {
      try {
        const prodRes = await purchaseOrderApi.getProductReport({
          cursor: 0,
          limit: 100,
          filters: { search_text: '', start_date: '', end_date: '' },
          category_id: catId
        });
        newItems[index].productList =
          prodRes.data?.results?.data || prodRes.data?.results || [];
      } catch (error) {
        toast.error('Failed to load products');
      }
    }
    setItems(newItems);
  };

  const handleProductChange = (index, prodId) => {
    const newItems = [...items];
    const selected = newItems[index].productList.find(
      p => String(p.id) === String(prodId)
    );
    newItems[index] = {
      ...newItems[index],
      productId: prodId,
      optionId: '',
      sku: '',
      pricePerUnit: 0,
      uom: '',
      optionsList: []
    };

    if (selected) {
      newItems[index].uom =
        selected.uom_name || selected.unit || selected.UOM || '';
      newItems[index].sku = selected.SKU || selected.product_sku || '';
      let opts = selected.options;
      if (typeof opts === 'string') {
        try {
          opts = JSON.parse(opts);
        } catch (e) {
          console.log(e);
        }
      }

      let optsList = [];
      if (opts) {
        if (Array.isArray(opts)) {
          optsList = opts.map((opt, i) => {
            const name = opt.option_name || opt.name || `Option ${i + 1}`;
            return {
              value: name,
              label: name
            };
          });
        } else if (typeof opts === 'object') {
          optsList = Object.keys(opts).map(k => {
            const optVal = opts[k];
            const name =
              typeof optVal === 'object'
                ? optVal.option_name || optVal.name || k
                : String(optVal || k);
            return {
              value: k,
              label: name
            };
          });
        }
      }
      newItems[index].optionsList = optsList;

      // Check if product has options
      const hasOptions = hasProductOptions(selected);
      if (!hasOptions) {
        newItems[index].pricePerUnit =
          Number(selected.base_price) ||
          Number(selected.Price) ||
          Number(selected.price) ||
          0;
      } else {
        newItems[index].pricePerUnit = 0;
      }
    }
    newItems[index].stockValue =
      newItems[index].pricePerUnit * (Number(newItems[index].quantity) || 0);
    setItems(newItems);
  };

  const handleOptionChange = (index, optId) => {
    const newItems = [...items];
    newItems[index].optionId = optId;
    const selected = newItems[index].productList.find(
      p => String(p.id) === String(newItems[index].productId)
    );

    if (selected) {
      const hasOptions = hasProductOptions(selected);
      if (optId) {
        newItems[index].pricePerUnit = getProductOptionPrice(selected, optId);
      } else {
        newItems[index].pricePerUnit = hasOptions
          ? 0
          : Number(selected.base_price) ||
            Number(selected.Price) ||
            Number(selected.price) ||
            0;
      }
    }
    newItems[index].stockValue =
      newItems[index].pricePerUnit * (Number(newItems[index].quantity) || 0);
    setItems(newItems);
  };

  const handleValueChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value === '' ? '' : Number(value);
    const p = Number(newItems[index].pricePerUnit) || 0;
    const q = Number(newItems[index].quantity) || 0;
    newItems[index].stockValue = p * q;
    setItems(newItems);
  };

  const handleTextChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  const totalValue = items.reduce(
    (sum, it) => sum + (Number(it.stockValue) || 0),
    0
  );
  const totalQty = items.reduce(
    (sum, it) => sum + (Number(it.quantity) || 0),
    0
  );

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async (e, forceStatus) => {
    if (e) e.preventDefault();
    if (!supplierId) {
      toast.error('Supplier is required');
      return;
    }
    if (!warehouseId) {
      toast.error('Warehouse is required');
      return;
    }

    const validItems = items.filter(
      it => it.productCategoryId && it.productId && Number(it.quantity) > 0
    );
    if (validItems.length === 0) {
      toast.error('At least one product with quantity > 0 is required');
      return;
    }

    const payload = {
      po_date: poDate,
      delivery_date: deliveryDate || null,
      supplier_id: supplierId,
      warehouse_id: warehouseId,
      payment_mode: paymentMode || null,
      payment_duration: paymentDuration ? Number(paymentDuration) : null,
      notes: notes || null,
      status: forceStatus || status,
      items: validItems.map(it => ({
        product_category_id: it.productCategoryId,
        product_id: it.productId,
        option_id: it.optionId || null,
        sku: it.sku || null,
        quantity: Number(it.quantity),
        uom: it.uom || null,
        price_per_unit: Number(it.pricePerUnit),
        stock_value: Number(it.stockValue)
      }))
    };

    setSubmitting(true);
    try {
      if (isEditMode) {
        await purchaseOrderApi.updatePurchaseOrder(id, payload);
        toast.success('Purchase Order updated successfully');
      } else {
        await purchaseOrderApi.createPurchaseOrder(payload);
        toast.success('Purchase Order created successfully');
      }
      navigate(`/${tenant}/purchase-order`);
    } catch (err) {
      toast.error(
        err.response?.data?.message || 'Failed to save Purchase Order'
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ── Styles ────────────────────────────────────────────────────────────────
  const inputStyle = {
    borderRadius: '8px',
    padding: '8px 12px',
    border: '1.5px solid #D8E0F0',
    fontSize: '13px'
  };

  if (loading)
    return (
      <Container
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: '80vh' }}
      >
        <Spinner animation="border" variant="primary" />
      </Container>
    );

  return (
    <Container
      fluid
      className="py-4 px-4"
      style={{
        backgroundColor: '#F4F6FB',
        minHeight: '100vh',
        paddingBottom: '80px'
      }}
    >
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div>
          <h1 className="mb-1 fw-bold text-dark" style={{ fontSize: '22px' }}>
            {isEditMode ? 'Edit Purchase Order' : 'Create Purchase Order'}
          </h1>
          <div className="text-muted" style={{ fontSize: '12px' }}>
            Inventory &gt; Purchase Orders &gt; Form
          </div>
        </div>
        <Button
          variant="light"
          onClick={() => navigate(`/${tenant}/purchase-order`)}
          className="rounded-pill shadow-sm"
          style={{ fontSize: '13px' }}
        >
          <FontAwesomeIcon icon={faArrowLeft} className="me-2" /> Back
        </Button>
      </div>

      <Form onSubmit={e => handleSubmit(e)}>
        {/* Header Section */}
        <Card
          className="shadow-sm border-0 mb-4"
          style={{ borderRadius: '14px' }}
        >
          <Card.Header className="bg-white py-3 border-0">
            <h6 className="mb-0 fw-bold text-dark">Basic Details</h6>
          </Card.Header>
          <Card.Body className="p-4 pt-2">
            <Row className="g-3">
              <Col md={3}>
                <Form.Label
                  className="fw-semibold text-secondary mb-1"
                  style={{ fontSize: '12px' }}
                >
                  PO Code
                </Form.Label>
                <Form.Control
                  type="text"
                  value={poCode}
                  readOnly
                  disabled
                  style={{ ...inputStyle, backgroundColor: '#f8f9fa' }}
                />
              </Col>
              <Col md={3}>
                <Form.Label
                  className="fw-semibold text-secondary mb-1"
                  style={{ fontSize: '12px' }}
                >
                  PO Date <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="date"
                  value={poDate}
                  onChange={e => setPoDate(e.target.value)}
                  required
                  style={inputStyle}
                />
              </Col>
              <Col md={3}>
                <Form.Label
                  className="fw-semibold text-secondary mb-1"
                  style={{ fontSize: '12px' }}
                >
                  Supplier <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  value={supplierId}
                  onChange={e => setSupplierId(e.target.value)}
                  required
                  style={inputStyle}
                >
                  <option value="">Select Supplier</option>
                  {suppliers.map(s => (
                    <option
                      key={s.supplier_id || s.id || s.value}
                      value={s.supplier_id || s.id || s.value}
                    >
                      {s.supplier_name || s.name || s.label}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Label
                  className="fw-semibold text-secondary mb-1"
                  style={{ fontSize: '12px' }}
                >
                  Warehouse / Center <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  value={warehouseId}
                  onChange={e => setWarehouseId(e.target.value)}
                  required
                  style={inputStyle}
                >
                  <option value="">Select Warehouse</option>
                  {warehouses.map(w => (
                    <option key={w.id || w.value} value={w.id || w.value}>
                      {w.name || w.label}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Label
                  className="fw-semibold text-secondary mb-1"
                  style={{ fontSize: '12px' }}
                >
                  Delivery Date
                </Form.Label>
                <Form.Control
                  type="date"
                  value={deliveryDate}
                  onChange={e => setDeliveryDate(e.target.value)}
                  style={inputStyle}
                />
              </Col>
              <Col md={3}>
                <Form.Label
                  className="fw-semibold text-secondary mb-1"
                  style={{ fontSize: '12px' }}
                >
                  Payment Mode
                </Form.Label>
                <Form.Select
                  value={paymentMode}
                  onChange={e => setPaymentMode(e.target.value)}
                  style={inputStyle}
                >
                  <option value="">Select Mode</option>
                  <option value="Cash">Cash</option>
                  <option value="Bank Transfer">Bank Transfer</option>
                  <option value="Mobile Money">Mobile Money</option>
                  <option value="Credit">Credit</option>
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Label
                  className="fw-semibold text-secondary mb-1"
                  style={{ fontSize: '12px' }}
                >
                  Payment Duration (Days)
                </Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  value={paymentDuration}
                  onChange={e => setPaymentDuration(e.target.value)}
                  placeholder="e.g. 30"
                  style={inputStyle}
                />
              </Col>
              <Col md={3}>
                <Form.Label
                  className="fw-semibold text-secondary mb-1"
                  style={{ fontSize: '12px' }}
                >
                  Notes
                </Form.Label>
                <Form.Control
                  type="text"
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  placeholder="Remarks..."
                  style={inputStyle}
                />
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Line Items Section */}
        <Card
          className="shadow-sm border-0 mb-4"
          style={{ borderRadius: '14px' }}
        >
          <Card.Header className="bg-white py-3 border-0 d-flex justify-content-between align-items-center">
            <h6 className="mb-0 fw-bold text-dark">Line Items</h6>
            <Button
              variant="outline-primary"
              size="sm"
              className="rounded-pill"
              onClick={handleAddItem}
              style={{ fontSize: '12px' }}
            >
              <FontAwesomeIcon icon={faPlus} className="me-2" /> Add Item
            </Button>
          </Card.Header>
          <Card.Body className="p-0">
            <div className="table-responsive" style={{ overflowX: 'auto' }}>
              <Table
                className="mb-0 align-middle"
                style={{ fontSize: '13px', minWidth: '1000px' }}
              >
                <thead style={{ backgroundColor: '#F8FAFC' }}>
                  <tr>
                    <th className="py-3 ps-4 text-secondary">
                      Product Category
                    </th>
                    <th className="py-3 text-secondary">Product</th>
                    <th className="py-3 text-secondary">Option</th>
                    <th
                      className="py-3 text-secondary"
                      style={{ width: '120px' }}
                    >
                      SKU
                    </th>
                    <th
                      className="py-3 text-secondary"
                      style={{ width: '100px' }}
                    >
                      Qty
                    </th>
                    <th
                      className="py-3 text-secondary"
                      style={{ width: '80px' }}
                    >
                      UoM
                    </th>
                    <th
                      className="py-3 text-secondary"
                      style={{ width: '120px' }}
                    >
                      Unit Price
                    </th>
                    <th
                      className="py-3 text-secondary text-end"
                      style={{ width: '120px' }}
                    >
                      Total (UGX)
                    </th>
                    <th
                      className="py-3 pe-4 text-center"
                      style={{ width: '60px' }}
                    ></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, index) => (
                    <tr
                      key={item.tempId}
                      style={{ borderBottom: '1px solid #F1F5F9' }}
                    >
                      <td className="ps-4">
                        <Form.Select
                          value={item.productCategoryId}
                          onChange={e =>
                            handleCategoryChange(index, e.target.value)
                          }
                          required
                          style={inputStyle}
                        >
                          <option value="">Select Category</option>
                          {categories.map(c => (
                            <option key={c.value} value={c.value}>
                              {c.label}
                            </option>
                          ))}
                        </Form.Select>
                      </td>
                      <td>
                        <Form.Select
                          value={item.productId}
                          onChange={e =>
                            handleProductChange(index, e.target.value)
                          }
                          required
                          disabled={!item.productCategoryId}
                          style={inputStyle}
                        >
                          <option value="">Select Product</option>
                          {item.productList?.map(p => (
                            <option key={p.id} value={p.id}>
                              {p['Product Name'] || p.product_name || p.name}{' '}
                              {p['SKU'] ? `(${p['SKU']})` : ''}
                            </option>
                          ))}
                        </Form.Select>
                      </td>
                      <td>
                        <Form.Select
                          value={item.optionId}
                          onChange={e =>
                            handleOptionChange(index, e.target.value)
                          }
                          disabled={
                            !item.productId || item.optionsList?.length === 0
                          }
                          style={inputStyle}
                        >
                          <option value="">
                            {item.optionsList?.length ? 'Select Option' : 'N/A'}
                          </option>
                          {item.optionsList?.map(o => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </Form.Select>
                      </td>
                      <td>
                        <Form.Control
                          type="text"
                          placeholder="SKU"
                          value={item.sku}
                          readOnly
                          disabled
                          style={{ ...inputStyle, backgroundColor: '#F8FAFC' }}
                        />
                      </td>
                      <td>
                        <Form.Control
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.quantity}
                          onChange={e =>
                            handleValueChange(index, 'quantity', e.target.value)
                          }
                          required
                          style={inputStyle}
                        />
                      </td>
                      <td>
                        <Form.Control
                          type="text"
                          value={item.uom}
                          readOnly
                          disabled
                          style={{ ...inputStyle, backgroundColor: '#F8FAFC' }}
                        />
                      </td>
                      <td>
                        <Form.Control
                          type="number"
                          value={item.pricePerUnit}
                          readOnly
                          disabled
                          style={{ ...inputStyle, backgroundColor: '#F8FAFC' }}
                        />
                      </td>
                      <td className="text-end fw-bold text-dark">
                        {Math.round(item.stockValue).toLocaleString()}
                      </td>
                      <td className="pe-4 text-center">
                        <Button
                          variant="link"
                          className="text-danger p-0"
                          onClick={() => handleRemoveItem(index)}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot
                  style={{
                    backgroundColor: '#F8FAFC',
                    borderTop: '2px solid #E2E8F0'
                  }}
                >
                  <tr>
                    <td colSpan={4} className="fw-bold text-end py-3">
                      Totals:
                    </td>
                    <td className="fw-bold text-primary py-3">
                      {Math.round(totalQty).toLocaleString()}
                    </td>
                    <td colSpan={2}></td>
                    <td className="text-end fw-bold text-primary py-3">
                      {Math.round(totalValue).toLocaleString()}
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </Table>
            </div>
          </Card.Body>
        </Card>

        {/* Footer Actions */}
        <div className="d-flex justify-content-end gap-3 mt-4 mb-5">
          <Button
            variant="outline-secondary"
            className="rounded-pill px-4 py-2"
            onClick={() => navigate(`/${tenant}/purchase-order`)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            className="rounded-pill px-4 py-2 d-flex align-items-center gap-2"
            onClick={e => handleSubmit(e, 'submitted')}
            disabled={submitting}
          >
            {submitting ? (
              <Spinner size="sm" animation="border" />
            ) : (
              <FontAwesomeIcon icon={faSave} />
            )}
            {isEditMode ? 'Update Purchase Order' : 'Submit Purchase Order'}
          </Button>
        </div>
      </Form>
    </Container>
  );
}
